import { AdminCatalogSelectedActionParams, Release, TableColumn } from "../../../../types"
import {
  ADMIN_PERMISSIONS,
  RELEASE_STATUSES,
  releaseEditStatusOptions,
  releaseStatusOptions,
  TABLE_COLUMN_TYPE
} from "../../../../constants"
import { KButton, KSpan, KDropdown, KTooltip } from "kahuna-base-react-components"
import React from "react"
import { useNavigate } from "react-router-dom"
import { checkAdminPermission, formatIsoDate, hashids } from "../../../../utility"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
//@ts-ignore
import CheckCircleGreen from "../../../../assets/admin_icons/check-circle.svg"
//@ts-ignore
import CloseCircle from "../../../../assets/admin_icons/close-circle.svg"
//@ts-ignore
import TrashIcon from "../../../../assets/admin_icons/trash-white.svg"
//@ts-ignore
import AppleMusicIcon from "../../../../assets/release-platform-icons/Apple-Music.png"
//@ts-ignore
import CILogoIcon from "../../../../assets/release-platform-icons/Ci-Logo.jpeg"
import UploadOptionsMenu from "./dropdown-menu/UploadOptionsMenu"
import ImageWithFallback from "../../../ui/components/ImageWithFallback"
import ReleaseCoverEditRequestBadge from "./ReleaseCoverEditRequestBadge"
//@ts-ignore
import RefreshIcon from "../../../../assets/admin_icons/refresh.svg"

const adminCatalogColumns = (
  onSubmitLabelcamp: (release: Release) => void,
  onSubmitBmv: (release: Release) => void,
  onSubmitMesamMsg: (release: Release) => void,
  onSubmitEditorialPitching: (release: Release) => void,
  onSubmitState51: (release: Release) => void,
  onSubmitMarketing: (release: Release) => void,
  setSelectedActionParams: (params: AdminCatalogSelectedActionParams) => void,
  onReloadLabelCamp: (title: string, productId: string, skipConfirmation: boolean) => void,
  onClickSpotifyColumn: (upc: string) => void,
  onClickAppleMusicColumn: (upc: string) => void,
  onClickCIColumn: (upc: string) => void,
  setSpotifyArtistModalReleaseId: (artistId?: number) => void,
  onApproveEditRequest: (release: Release) => void,
  adminPermissions?: string
): TableColumn[] => {
  const navigate = useNavigate()
  const releaseNameColumn: TableColumn = {
    header: "Release",
    headerStyle: {
      textAlign: "left",
      width: "max-content"
    },
    sortable: true,
    sortFilterField: "title",
    type: TABLE_COLUMN_TYPE.FUNCTION,
    render: (row: Release) => {
      const artists = row?.artistsList || []
      const canWrite = checkAdminPermission(ADMIN_PERMISSIONS.ASSET_REGISTRY_WRITE, adminPermissions)
      return (
        <div data-testid="find-track" className="flex w-max">
          <div className="flex flex-row gap-3 items-center">
            <ReleaseCoverEditRequestBadge
              coverFileUrl={row?.coverFileUrl}
              hasEditRequest={!!row?.artistEditRequest}
              onApprove={() => onApproveEditRequest(row)}
            />
            <div className="flex flex-col justify-between gap-0.5">
              <div
                className="flex gap-1"
                style={{
                  cursor: canWrite ? "pointer" : "default"
                }}
                onClick={() => {
                  if (canWrite) {
                    navigate(`${hashids.encode(row?.id?.toString() || "")}`)
                  }
                }}
              >
                <KSpan text={row.title} color="#000" fontWeight={500} />
                {row.version && <KSpan text={`(${row.version})`} />}
              </div>

              <div className="flex flex-row gap-0.5">
                <div className="rounded-[4px] bg-[#F7F7F7] p-1 flex justify-center items-center">
                  <KSpan
                    text={`${row.totalTrack} ${row.totalTrack > 1 ? "songs" : "song"}`}
                    color="#111"
                    fontSize={11}
                    fontWeight={500}
                    lineHeight="12px"
                    letterSpacing="0.22px"
                  />
                </div>
                <div
                  className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-auto no-parent-trigger"
                  onClick={() => setSpotifyArtistModalReleaseId(row.id)}
                >
                  <KSpan
                    text={
                      artists
                        ?.slice(0, 2)
                        ?.map((artist) => artist?.name)
                        .join(", ") || "-"
                    }
                    color="#005991"
                    fontSize={11}
                    fontWeight={500}
                    lineHeight="12px"
                    letterSpacing="0.22px"
                  />
                  {artists?.length > 2 && (
                    <KTooltip
                      backgroundColor="#000"
                      arrowColor="#000"
                      position="bottom"
                      content={
                        <div className="w-max flex flex-col gap-1 items-start">
                          {artists.slice(2, artists.length).map((artist, index: number) => {
                            return (
                              <KSpan
                                key={`${artist?.name}-${index}`}
                                text={artist?.name}
                                fontSize={12}
                                fontWeight={500}
                                lineHeight="16px"
                                color="#FFF"
                              />
                            )
                          })}
                        </div>
                      }
                    >
                      {" "}
                      <div className="flex items-center">
                        <KSpan
                          text={`, (+${artists?.length - 2})`}
                          color="#111"
                          fontSize={11}
                          fontWeight={500}
                          lineHeight="12px"
                          letterSpacing="0.22px"
                        />
                      </div>
                    </KTooltip>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }
  }

  const releaseDateColumn: TableColumn = {
    header: "Release Date",
    headerStyle: {
      textAlign: "left",
      width: "max-content"
    },
    sortable: true,
    sortFilterField: "release_date",
    type: TABLE_COLUMN_TYPE.FUNCTION,
    render: (row: Release) => {
      return (
        <div className="flex">
          <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-max no-parent-trigger">
            <KSpan
              text={formatIsoDate(row?.releaseDate, true)}
              color="#111"
              fontSize={11}
              fontWeight={500}
              lineHeight="12px"
              letterSpacing="0.22px"
            />
          </div>
        </div>
      )
    }
  }

  const genreColumn: TableColumn = {
    header: "Genre",
    headerStyle: {
      textAlign: "left",
      width: "max-content"
    },
    sortable: true,
    sortFilterField: "genre",
    type: TABLE_COLUMN_TYPE.FUNCTION,
    render: (row: Release) => {
      return (
        <div className="flex">
          <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-max no-parent-trigger">
            <KSpan
              text={row.genre || "-"}
              color="#111"
              fontSize={11}
              fontWeight={500}
              lineHeight="12px"
              letterSpacing="0.22px"
            />
          </div>
        </div>
      )
    }
  }

  const takedownColumn: TableColumn = {
    header: "Takedown",
    headerStyle: {
      textAlign: "left",
      width: "max-content"
    },
    sortable: true,
    sortFilterField: "takedown",
    type: TABLE_COLUMN_TYPE.FUNCTION,
    render: (row: Release) => {
      return (
        <div className="flex">
          <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-max no-parent-trigger">
            <KSpan
              text={row.takedown ? "√" : "-"}
              color="#111"
              fontSize={11}
              fontWeight={500}
              lineHeight="12px"
              letterSpacing="0.22px"
            />
          </div>
        </div>
      )
    }
  }

  const labelColumn: TableColumn = {
    header: "Label",
    headerStyle: {
      textAlign: "left",
      width: "max-content"
    },
    sortable: true,
    sortFilterField: "label__name",
    type: TABLE_COLUMN_TYPE.FUNCTION,
    render: (row: Release) => {
      return (
        <div className="flex">
          <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-auto no-parent-trigger">
            <KSpan
              text={row.labelDetail?.name || ""}
              color="#111"
              fontSize={11}
              fontWeight={500}
              lineHeight="12px"
              letterSpacing="0.22px"
            />
          </div>
        </div>
      )
    }
  }

  const upcColumn: TableColumn = {
    header: "UPC",
    headerStyle: {
      textAlign: "left",
      width: "max-content"
    },
    sortable: true,
    sortFilterField: "upc",
    type: TABLE_COLUMN_TYPE.FUNCTION,
    render: (row: Release) => {
      return (
        <div className="flex">
          <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-auto no-parent-trigger">
            <KSpan
              text={row.upc}
              color="#111"
              fontSize={11}
              fontWeight={500}
              lineHeight="12px"
              letterSpacing="0.22px"
            />
          </div>
        </div>
      )
    }
  }

  const ciLinkColumn: TableColumn = {
    header: "CI Link",
    headerStyle: {
      textAlign: "left",
      width: "max-content"
    },
    cellStyle: {
      fontWeight: 400,
      lineHeight: "20px",
      color: "#111827"
    },
    type: TABLE_COLUMN_TYPE.FUNCTION,
    render: (row: Release) => {
      return (
        <div className="flex gap-2 no-parent-trigger">
          <div style={{ width: 44, display: "grid", alignItems: "center" }}>
            <img
              onClick={() => {
                if (!row?.upc) return
                onClickCIColumn(row.upc)
              }}
              src={CILogoIcon}
              alt={`${row.id}_ci_link`}
              style={{ width: 24, height: 24, minWidth: 24, minHeight: 24, cursor: "pointer", borderRadius: 5 }}
            />
          </div>
        </div>
      )
    }
  }

  const spotifyColumn: TableColumn = {
    header: "Spotify Link",
    headerStyle: {
      textAlign: "left",
      width: "max-content"
    },
    cellStyle: {
      fontWeight: 400,
      lineHeight: "20px",
      color: "#111827"
    },
    type: TABLE_COLUMN_TYPE.FUNCTION,
    render: (row: Release) => {
      return (
        <div className="flex gap-2 no-parent-trigger">
          <div style={{ width: 44, display: "grid", alignItems: "center" }}>
            <img
              onClick={() => {
                if (!row?.upc) return
                onClickSpotifyColumn(row.upc)
              }}
              src={"/account_icons/social_media_icons/spotify.svg"}
              alt={`${row.id}_spotify_link`}
              style={{ width: 24, height: 24, minWidth: 24, minHeight: 24, cursor: "pointer" }}
            />
          </div>
        </div>
      )
    }
  }

  const appleMusicColumn: TableColumn = {
    header: "Apple Music Link",
    headerStyle: {
      textAlign: "left",
      width: "max-content"
    },
    cellStyle: {
      fontWeight: 400,
      lineHeight: "20px",
      color: "#111827"
    },
    type: TABLE_COLUMN_TYPE.FUNCTION,
    render: (row: Release) => {
      return (
        <div className="flex gap-2 no-parent-trigger">
          <div style={{ width: 44, display: "grid", alignItems: "center" }}>
            <img
              onClick={() => {
                if (!row?.upc) return
                onClickAppleMusicColumn(row.upc)
              }}
              src={AppleMusicIcon}
              alt={`${row.id}_spotify_link`}
              style={{ width: 24, height: 24, minWidth: 24, minHeight: 24, cursor: "pointer" }}
            />
          </div>
        </div>
      )
    }
  }

  const userLogColumn: TableColumn = {
    header: "History",
    headerStyle: {
      textAlign: "left",
      width: "max-content"
    },
    cellStyle: {
      fontWeight: 400,
      lineHeight: "20px",
      color: "#111827"
    },
    type: TABLE_COLUMN_TYPE.FUNCTION,
    render: (row: Release) => {
      return (
        <div className="flex gap-2 no-parent-trigger">
          <div style={{ width: 44, display: "grid", alignItems: "center" }}>
            <img
              onClick={() => window.open(`/admin/user-asset?log_upc=${row.upc}`, "_blank")}
              src={"/catalog_icons/history.svg"}
              alt={`${row.id}_history`}
              style={{ width: 20, height: "fit-content", cursor: "pointer" }}
            />
          </div>
        </div>
      )
    }
  }

  const labelCampId: TableColumn = {
    header: "Label Camp",
    headerStyle: {
      textAlign: "left",
      width: "max-content"
    },
    cellStyle: {
      fontWeight: 400,
      lineHeight: "20px",
      color: "#111827"
    },
    type: TABLE_COLUMN_TYPE.FUNCTION,
    render: (row: Release) => {
      return (
        <div className="flex gap-2 no-parent-trigger">
          <div style={{ width: 44, display: "grid", alignItems: "center" }}>
            <img
              onClick={() => window.open(`https://labelcamp.idol.io/catalog/products/${row.productId}`, "_blank")}
              src={"/catalog_icons/idol.png"}
              alt={`${row.id}_idol`}
              style={{ width: 20, height: "fit-content", cursor: "pointer" }}
            />
          </div>
        </div>
      )
    }
  }

  const bmvDriveId: TableColumn = {
    header: "BMV",
    headerStyle: {
      textAlign: "left",
      width: "max-content"
    },
    cellStyle: {
      fontWeight: 400,
      lineHeight: "20px",
      color: "#111827"
    },
    type: TABLE_COLUMN_TYPE.FUNCTION,
    render: (row: Release) => {
      return (
        <div className="flex gap-2 no-parent-trigger">
          <div style={{ width: 44, display: "grid", alignItems: "center" }}>
            {row.driveId ? (
              <img
                onClick={() => window.open(`https://drive.google.com/drive/folders/${row.driveId}`, "_blank")}
                src={"/catalog_icons/drive_icon.png"}
                alt={`${row.id}_bmv_drive`}
                style={{ width: 24, height: "fit-content", cursor: "pointer" }}
              />
            ) : (
              <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-max no-parent-trigger">
                <KSpan
                  text={"-"}
                  color="#111"
                  fontSize={11}
                  fontWeight={500}
                  lineHeight="12px"
                  letterSpacing="0.22px"
                />
              </div>
            )}
          </div>
        </div>
      )
    }
  }

  const publishingDriveId: TableColumn = {
    header: "MESAM MSG",
    headerStyle: {
      textAlign: "left",
      width: "max-content"
    },
    cellStyle: {
      fontWeight: 400,
      lineHeight: "20px",
      color: "#111827"
    },
    type: TABLE_COLUMN_TYPE.FUNCTION,
    render: (row: Release) => {
      return (
        <div className="flex gap-2 no-parent-trigger">
          <div style={{ width: 44, display: "grid", alignItems: "center" }}>
            {row.mesamMsgId ? (
              <img
                src={"/catalog_icons/drive_icon.png"}
                alt={`${row.id}_publishing_drive`}
                style={{ width: 24, cursor: "pointer" }}
                onClick={() => window.open(`https://drive.google.com/drive/folders/${row.mesamMsgId}`, "_blank")}
              />
            ) : (
              <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-max no-parent-trigger">
                <KSpan
                  text={"-"}
                  color="#111"
                  fontSize={11}
                  fontWeight={500}
                  lineHeight="12px"
                  letterSpacing="0.22px"
                />
              </div>
            )}
          </div>
        </div>
      )
    }
  }

  const editorialPitchingDriveId: TableColumn = {
    header: "Editorial Pitching",
    headerStyle: {
      textAlign: "left",
      width: "max-content"
    },
    cellStyle: {
      fontWeight: 400,
      lineHeight: "20px",
      color: "#111827"
    },
    type: TABLE_COLUMN_TYPE.FUNCTION,
    render: (row: Release) => {
      return (
        <div className="flex gap-2 no-parent-trigger">
          <div style={{ width: 44, display: "grid", alignItems: "center" }}>
            {row.editorialPitchingId ? (
              <img
                src={"/catalog_icons/drive_icon.png"}
                alt={`${row.id}_pitching_drive`}
                style={{ width: 24, cursor: "pointer" }}
                onClick={() =>
                  window.open(`https://docs.google.com/spreadsheets/d/${row.editorialPitchingId}`, "_blank")
                }
              />
            ) : (
              <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-max no-parent-trigger">
                <KSpan
                  text={"-"}
                  color="#111"
                  fontSize={11}
                  fontWeight={500}
                  lineHeight="12px"
                  letterSpacing="0.22px"
                />
              </div>
            )}
          </div>
        </div>
      )
    }
  }

  const state51Column: TableColumn = {
    header: "State51 ID",
    headerStyle: {
      textAlign: "left",
      width: "max-content"
    },
    cellStyle: {
      fontWeight: 400,
      lineHeight: "20px",
      color: "#111827"
    },
    type: TABLE_COLUMN_TYPE.FUNCTION,
    render: (row: Release) => {
      return (
        <div className="flex gap-2 no-parent-trigger" style={{ alignItems: "center" }}>
          <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-max no-parent-trigger">
            <KSpan
              text={row.state51Id || "-"}
              color="#111"
              fontSize={11}
              fontWeight={500}
              lineHeight="12px"
              letterSpacing="0.22px"
            />
          </div>
        </div>
      )
    }
  }

  const state51DeliveryColumn: TableColumn = {
    header: "State51 Delivery",
    headerStyle: {
      textAlign: "left",
      width: "max-content"
    },
    cellStyle: {
      fontWeight: 400,
      lineHeight: "20px",
      color: "#111827"
    },
    type: TABLE_COLUMN_TYPE.FUNCTION,
    render: (row: Release) => {
      return (
        <div className="flex gap-2 no-parent-trigger" style={{ alignItems: "center" }}>
          <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-max no-parent-trigger">
            <KTooltip
              width="250px"
              position="bottom"
              children={
                <KSpan
                  text={row.state51Delivery?.status || "-"}
                  color="#111"
                  fontSize={11}
                  fontWeight={500}
                  lineHeight="12px"
                  letterSpacing="0.22px"
                />
              }
              content={<KSpan text={row.state51Delivery?.message || ""} />}
            />
          </div>
        </div>
      )
    }
  }

  const marketingDriveId: TableColumn = {
    header: "Marketing",
    headerStyle: {
      textAlign: "left",
      width: "max-content"
    },
    cellStyle: {
      fontWeight: 400,
      lineHeight: "20px",
      color: "#111827"
    },
    type: TABLE_COLUMN_TYPE.FUNCTION,
    render: (row: Release) => {
      let imageCount = 0
      let videoCount = 0
      let pressReleaseCount = 0
      if (row.releaseMarketing?.image1) imageCount += 1
      if (row.releaseMarketing?.image2) imageCount += 1
      if (row.releaseMarketing?.image3) imageCount += 1
      if (row.releaseMarketing?.video1) videoCount += 1
      if (row.releaseMarketing?.video2) videoCount += 1
      if (row.releaseMarketing?.pressRelease) pressReleaseCount += 1

      return (
        <div className="flex gap-2 no-parent-trigger">
          <div style={{ width: 44, display: "grid", alignItems: "center" }}>
            {row.marketingId ? (
              <div className="flex items-center gap-1">
                <img
                  src={"/catalog_icons/drive_icon.png"}
                  alt={`${row.id}_marketing_drive`}
                  style={{ width: 24, cursor: "pointer" }}
                  onClick={() => window.open(`https://drive.google.com/drive/folders/${row.marketingId}`, "_blank")}
                />
                <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-max no-parent-trigger">
                  <KSpan fontSize={11} text={imageCount.toString()} />
                </div>
                <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-max no-parent-trigger">
                  <KSpan fontSize={11} text={videoCount.toString()} />
                </div>
                <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-max no-parent-trigger">
                  <KSpan fontSize={11} text={pressReleaseCount.toString()} />
                </div>
              </div>
            ) : (
              <div className="rounded-[4px] bg-[#F7F7F7] flex items-center p-1 w-max no-parent-trigger">
                <KSpan
                  text={"-"}
                  color="#111"
                  fontSize={11}
                  fontWeight={500}
                  lineHeight="12px"
                  letterSpacing="0.22px"
                />
              </div>
            )}
          </div>
        </div>
      )
    }
  }

  const statusColumn: TableColumn = {
    header: "LabelCamp Status",
    headerStyle: {
      textAlign: "center",
      width: "max-content"
    },
    sortable: true,
    sortFilterField: "status",
    type: TABLE_COLUMN_TYPE.FUNCTION,
    render: (row: Release) => {
      const statusOptions: KSelectOption[] = releaseStatusOptions.map((statusOption, index) => {
        return { label: statusOption.label, value: index, value2: statusOption.value, icon: statusOption.iconColored }
      })

      return (
        <div className="relative no-parent-trigger">
          {checkAdminPermission(ADMIN_PERMISSIONS.ASSET_REGISTRY_WRITE, adminPermissions) ? (
            <KDropdown
              dataTestId="admin-release-status-dropdown"
              width={135}
              options={statusOptions}
              onSelect={(selected) => {
                const selectedStatus = (selected as KSelectOption).value2
                if (!selected || !selectedStatus || !row.id) return
                setSelectedActionParams({
                  type: "statusUpdate",
                  id: row.id,
                  status: selectedStatus,
                  editStatus: row.editStatus,
                  title: row.title,
                  checked: false
                })
              }}
              selected={statusOptions.find((option) => row.status === option.value2)}
              rightIcon="/analytics_icons/caret-down-new.svg"
              menuWidth={160}
              menuLeftMargin={-8}
              enableIndicator
            />
          ) : (
            <div className="flex gap-1 mr-4">
              <img src={statusOptions.find((option) => row.status === option.value2)?.icon || ""} />
              <KSpan text={statusOptions.find((option) => row.status === option.value2)?.label || ""} />
            </div>
          )}
        </div>
      )
    }
  }

  const editStatusColumn: TableColumn = {
    header: "User Edit Status",
    headerStyle: {
      textAlign: "center",
      width: "max-content"
    },
    sortable: true,
    sortFilterField: "edit_status",
    type: TABLE_COLUMN_TYPE.FUNCTION,
    render: (row: Release) => {
      const statusOptions: KSelectOption[] = releaseEditStatusOptions.map((statusOption, index) => {
        return { label: statusOption.label, value: index, value2: statusOption.value, icon: statusOption.iconColored }
      })

      return (
        <div className="relative no-parent-trigger">
          {(row.status === RELEASE_STATUSES.REVIEWING || row.status === RELEASE_STATUSES.ACTIVE) &&
          checkAdminPermission(ADMIN_PERMISSIONS.ASSET_REGISTRY_WRITE, adminPermissions) ? (
            <KDropdown
              dataTestId="admin-release-edit-status-dropdown"
              width={145}
              options={statusOptions}
              onSelect={(selected) => {
                const selectedStatus = (selected as KSelectOption).value2
                if (!selected || !selectedStatus || !row.id) return
                setSelectedActionParams({
                  type: "editStatusUpdate",
                  id: row.id,
                  status: row.status,
                  editStatus: selectedStatus,
                  title: row.title,
                  checked: false
                })
              }}
              selected={statusOptions.find((option) => row.editStatus === option.value2)}
              rightIcon="/analytics_icons/caret-down-new.svg"
              menuWidth={160}
              menuLeftMargin={-8}
              enableIndicator
            />
          ) : (
            <div className="flex gap-1 mr-4">
              <img src={statusOptions.find((option) => row.editStatus === option.value2)?.icon || ""} />
              <KSpan text={statusOptions.find((option) => row.editStatus === option.value2)?.label || ""} />
            </div>
          )}
        </div>
      )
    }
  }

  const showColumn: TableColumn = {
    header: "Show",
    headerStyle: {
      textAlign: "center",
      width: "max-content",
      color: "transparent"
    },
    type: TABLE_COLUMN_TYPE.FUNCTION,
    render: (row: Release) => (
      <div className="flex flex-row gap-2 shrink-0 w-full no-parent-trigger">
        <KButton
          dataTestId="admin-release-show-button"
          icon={row.labelCamp ? CheckCircleGreen : CloseCircle}
          padding="8px"
          height="36px"
          width="36px"
          disabled={!checkAdminPermission(ADMIN_PERMISSIONS.ASSET_REGISTRY_WRITE, adminPermissions)}
          background="#FFF"
          onClick={() => {
            if (!row.id) return
            setSelectedActionParams({
              type: "labelCampUpdate",
              id: row.id,
              status: undefined,
              editStatus: undefined,
              title: row.title,
              checked: !row.labelCamp
            })
          }}
          border="1px solid #F3F3F3"
        />
      </div>
    )
  }

  const uploadColumn: TableColumn = {
    header: "Upload",
    headerStyle: {
      textAlign: "center",
      width: "max-content",
      color: "transparent"
    },
    type: TABLE_COLUMN_TYPE.FUNCTION,
    render: (row: Release) => (
      <div className="flex flex-row gap-2 shrink-0 w-full no-parent-trigger">
        {checkAdminPermission(ADMIN_PERMISSIONS.ASSET_REGISTRY_FORCE_UPLOAD, adminPermissions) && (
          <UploadOptionsMenu
            onSubmitLabelcamp={() => {
              onSubmitLabelcamp(row)
            }}
            onSubmitBmv={() => {
              onSubmitBmv(row)
            }}
            onSubmitMesamMsg={() => {
              onSubmitMesamMsg(row)
            }}
            onSubmitEditorialPitching={() => {
              onSubmitEditorialPitching(row)
            }}
            onSubmitState51={() => {
              onSubmitState51(row)
            }}
            onSubmitMarketing={() => {
              onSubmitMarketing(row)
            }}
          />
        )}
      </div>
    )
  }

  const reloadColumn: TableColumn = {
    header: "Reload",
    headerStyle: {
      textAlign: "center",
      width: "max-content",
      color: "transparent"
    },
    type: TABLE_COLUMN_TYPE.FUNCTION,
    render: (row: Release) => (
      <div onClick={(event) => event.stopPropagation()}>
        <KButton
          dataTestId="admin-release-reload-button"
          icon={RefreshIcon}
          onClick={() => row.productId && onReloadLabelCamp(row.title, row.productId, false)}
          padding="6px"
          width="36px"
          height="36px"
          background="white"
        />
      </div>
    )
  }

  const deleteActionColumn: TableColumn = {
    header: "",
    renderOnlyWhenHovered: true,
    headerStyle: {
      textAlign: "center",
      width: "max-content",
      minWidth: "60px"
    },
    type: TABLE_COLUMN_TYPE.FUNCTION,
    render: (row: Release) => (
      <div className="flex flex-row gap-2 shrink-0 w-full no-parent-trigger">
        {checkAdminPermission(ADMIN_PERMISSIONS.ASSET_REGISTRY_DELETE, adminPermissions) && (
          <KButton
            dataTestId="admin-release-delete-button"
            icon={TrashIcon}
            padding="8px"
            height="36px"
            width="36px"
            background="#FF5865"
            hoverBackground="#A33841"
            activeBackground="#FF5865"
            onClick={() => {
              setSelectedActionParams({
                type: "releaseDelete",
                id: row.id,
                status: undefined,
                editStatus: undefined,
                title: row.title,
                checked: false
              })
            }}
          />
        )}
      </div>
    )
  }

  const columns = [
    releaseNameColumn,
    releaseDateColumn,
    genreColumn,
    takedownColumn,
    labelColumn,
    upcColumn,
    spotifyColumn,
    ciLinkColumn,
    appleMusicColumn,
    userLogColumn,
    labelCampId,
    bmvDriveId,
    publishingDriveId,
    editorialPitchingDriveId,
    state51Column,
    state51DeliveryColumn,
    marketingDriveId,
    statusColumn,
    editStatusColumn,
    showColumn,
    reloadColumn
  ]
  if (checkAdminPermission(ADMIN_PERMISSIONS.ASSET_REGISTRY_FORCE_UPLOAD, adminPermissions)) {
    columns.push(uploadColumn)
  }
  if (checkAdminPermission(ADMIN_PERMISSIONS.ASSET_REGISTRY_DELETE, adminPermissions)) {
    columns.push(deleteActionColumn)
  }
  return columns
}

export default adminCatalogColumns
